import {AbstractControl, FormGroup, ValidationErrors, ValidatorFn, Validators} from "@angular/forms";
import {ChangeDetectorRef} from "@angular/core";
import {Subscription} from "rxjs";
import libphonenumber from 'google-libphonenumber';
import moment from 'moment';
const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

export class BSValidators extends Validators{

  public static web(core: string = null): ValidatorFn{
    const regex = new RegExp('^(https?:\\/\\/)?'+ // protocol
      (core ? '('+core+')' : '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|') + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))'+ // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*'+ // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?'+ // query string
      '(\\#[-a-z\\d_]*)?$','i'); // fragment locator

    return this.pattern(new RegExp(regex));
  }

  public static get email(): ValidatorFn{
    const regex = "(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|\"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])*\")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\\])"
    return this.pattern(new RegExp(regex));
  }

  public static get phoneValid(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} => {
      if(control.value && control.value.e164Number && control.value.countryCode) {
        const number = phoneUtil.parseAndKeepRawInput(control.value.e164Number);
        // isValidNumberForRegion() checks the number against each region's assigned-range
        // metadata, which google-libphonenumber does not keep exhaustively up to date: real
        // NL mobile numbers starting 06-7.../06-9... come back invalid (confirmed against the
        // library directly -- 06-1/06-5/06-6 pass, 06-7/06-9 do not). Rather than loosening
        // the check for every country/number type, accept exactly that gap via a direct
        // pattern match and keep isValidNumberForRegion as the real check everywhere else.
        const isKnownMetadataGap = control.value.countryCode === 'NL'
          && /^\+316\d{8}$/.test(control.value.e164Number);
        if(!phoneUtil.isValidNumberForRegion(number, control.value.countryCode) && !isKnownMetadataGap){
          return {
            phone: {
              invalidNumber: control.value.e164Number
            }
          };
        }
      }
      return null;
    }
  }

  /**
   * ngx-intl-tel-input's own bundled validator (registered whenever
   * [phoneValidation] is bound at all) has only two states: "true" runs its
   * own strict isValidNumberForRegion check unconditionally, and "false"
   * calls control.clearValidators() -- which wipes the control's ENTIRE
   * composed validator, including phoneValid above, not just the library's
   * own check (Angular's clearValidators() sets control.validator = null
   * outright). With [phoneValidation]="false" the field therefore validates
   * nothing at all after the first edit. Re-arm phoneValid immediately
   * whenever that happens; setValidators() only recombines the control's OWN
   * validators, not the directive-supplied ones, so this also permanently
   * excludes the library's validator from the control going forward.
   */
  public static armPhoneValidator(control: AbstractControl): Subscription {
    const rearm = () => {
      if(!control.validator){
        control.setValidators(BSValidators.phoneValid);
        control.updateValueAndValidity({onlySelf: true, emitEvent: false});
      }
    };
    const sub = control.valueChanges.subscribe(rearm);
    rearm();
    return sub;
  }

  public static checkPasswords(group: FormGroup) { // here we have the 'passwords' group gobl gobl
    const password = group.get('password').value;
    const confirmPassword = group.get('password_confirmation').value;

    return password === confirmPassword ? null : { notSame: true }
  }

  public static dateRange(): ValidatorFn {
    return (control: AbstractControl): {[key: string]: any} => {
      return !(control.valid && control.value && control.value.start && control.value.end && control.value.start < control.value.end) ? {
        dateRange: {
          value: control.value
        }
      } : null;
    };
  }

  public static dateOrder(group: AbstractControl): ValidationErrors | null{
    const a = group.get('start_on')?.value;
    const b = group.get('end_on')?.value;

    // start_on/end_on may hold a Date or a Moment depending on the control's
    // source (see Globals.fromDateStringToMoment); .getTime() only exists on
    // Date, so compare through moment() to accept either.
    return a && b && moment(a).valueOf() >= moment(b).valueOf() ? { dateorder: true } : null;
  }

  public static ifValue(controls: {[key: string]: any}): ValidatorFn{
    return (control: AbstractControl): {[key: string]: any} => {
      const errors: any = {};
      const req = BSValidators.required(control);
      for(let v in controls){
        let found: AbstractControl = null;
        let curhead = control.parent;
        if(curhead) {
          do {
            found = curhead.get(v);
            if (!found) {
              curhead = curhead.parent;
            }
          } while (curhead && !found);

          if (found && found.value === controls[v] && req) {
            return BSValidators.required(control);
            // errors[v] = req;
          }
        }
      }
      return null;
    };
  }

  public static minElements(num: number): ValidatorFn{
    return (control: AbstractControl): {[key: string]: any} => {
      return !(control.valid && control.value && control.value.length >= num) ? {
        minElements: {
          actual: control.value ? control.value.length : 0,
          needs: num
        }
      } : null;
    };
  }

  public static nextValidate(tovalue: any,
                             controls: { [key: string]: ((control: AbstractControl) => (ValidationErrors | null))[] },
                             detector: ChangeDetectorRef = null,
                             values: {[key: string]: any} = null) {
    return (control: AbstractControl): {[key: string]: any} => {

      let errors = null;
      for(let v in controls){
        let found: AbstractControl = null;
        let curhead = control.parent;
        if(curhead) {
          do {
            found = curhead.get(v);
            if (!found) {
              curhead = curhead.parent;
            }
          } while (curhead && !found);

          if(control.value === tovalue){
            found.setValidators(controls[v]);
            if(values && values[v]){
              found.setValue(values[v]);
            }
          }
          else{
            found.clearValidators();
            found.setValue(null);
          }
          found.updateValueAndValidity({
            onlySelf: true
          });
        }
      }

      if(detector) {
        detector.detectChanges();
      }
      return errors;
    };
  }
}
